import { useMemo } from "react"
import { ONE_DAY_IN_MS } from "@/v2-backoffice-client/constants"
import { useSearchParams } from "@/v2-backoffice-client/utils"
import type { ChurnRiskLevel } from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import type { ScoreLevel } from "@/v2-backoffice-client/usage/usage.constants"
import {
  CHURN_FILTER_LEVELS,
  SCORE_FILTER_LEVELS,
} from "@/v2-backoffice-client/portfolio/portfolio.constants"

/**
 * The Portfolio's global timeframes. Trailing windows end now; Q1–Q4 are
 * the CURRENT year's calendar quarters (sales runs on quarters). The
 * default matches the fixed 28-day window every profile-derived signal
 * (scores, churn, plays) is computed on.
 */
export type PortfolioTimeframeKey =
  | "24h"
  | "3d"
  | "7d"
  | "14d"
  | "28d"
  | "3m"
  | "6m"
  | "1y"
  | "q1"
  | "q2"
  | "q3"
  | "q4"

export const DEFAULT_TIMEFRAME: PortfolioTimeframeKey = "28d"

export const PORTFOLIO_TIMEFRAMES: {
  key: PortfolioTimeframeKey
  label: string
}[] = [
  { key: "24h", label: "Last 24 hours" },
  { key: "3d", label: "Last 3 days" },
  { key: "7d", label: "Last 7 days" },
  { key: "14d", label: "Last 14 days" },
  { key: "28d", label: "Last 28 days" },
  { key: "3m", label: "Last 3 months" },
  { key: "6m", label: "Last 6 months" },
  { key: "1y", label: "Last year" },
  { key: "q1", label: "Q1" },
  { key: "q2", label: "Q2" },
  { key: "q3", label: "Q3" },
  { key: "q4", label: "Q4" },
]

const TIMEFRAME_KEYS = new Set(PORTFOLIO_TIMEFRAMES.map((t) => t.key))

/**
 * A resolved window: `from` inclusive; `to` null = "up to now"; `days` is
 * the window length the daily charts pad to; `end` anchors the padding and
 * the 28d-vs-previous comparisons (window end for quarters, now otherwise).
 */
export type PortfolioWindow = {
  key: PortfolioTimeframeKey
  from: Date
  to: Date | null
  days: number
  end: Date
}

export function resolveTimeframe(key: PortfolioTimeframeKey): PortfolioWindow {
  const now = new Date()

  function trailing(days: number): PortfolioWindow {
    return {
      key,
      from: new Date(now.getTime() - days * ONE_DAY_IN_MS),
      to: null,
      days,
      end: now,
    }
  }

  function quarter(q: number): PortfolioWindow {
    const year = now.getFullYear()
    const from = new Date(year, (q - 1) * 3, 1)
    // Day 0 of the next quarter's first month = this quarter's last day.
    const lastDay = new Date(year, q * 3, 0)
    const to = new Date(year, q * 3, 0, 23, 59, 59)
    const days =
      Math.round((lastDay.getTime() - from.getTime()) / ONE_DAY_IN_MS) + 1
    return { key, from, to, days, end: lastDay }
  }

  switch (key) {
    case "24h":
      // Daily rows can't slice below a calendar day; pad the two days the
      // window can touch.
      return { ...trailing(1), days: 2 }
    case "3d":
      return trailing(3)
    case "7d":
      return trailing(7)
    case "14d":
      return trailing(14)
    case "28d":
      return trailing(28)
    case "3m":
      return trailing(91)
    case "6m":
      return trailing(182)
    case "1y":
      return trailing(365)
    case "q1":
      return quarter(1)
    case "q2":
      return quarter(2)
    case "q3":
      return quarter(3)
    case "q4":
      return quarter(4)
  }
}

/**
 * The Portfolio's global scope filters, applied across every sub-tab.
 * Deliberately WITHOUT a timeframe: every profile-derived signal (scores,
 * churn, plays) is computed upfront in Databricks on a fixed trailing-28d
 * window, so time selection lives only on the sections whose data can
 * actually re-window (daily activity, geography sign-ins) — see
 * {@link usePortfolioTimeframe}.
 */
export type PortfolioFilters = {
  /** ExternalCustomerIds to include; empty = whole portfolio. */
  customerIds: string[]
  /** ExternalCustomerIds to exclude. */
  excludeCustomerIds: string[]
  /** User ids to exclude (e.g. scripted accounts skewing a customer). */
  excludeUserIds: number[]
  /**
   * HubSpot company owners to keep — "my book" for a CSM/sales person;
   * empty = all. Matched against the raw customer groups (like the id
   * scopes), so every KPI, chart and play re-reads on the owner's book.
   */
  owners: string[]
  /** Churn-risk levels to keep (e.g. hide "no baseline"); empty = all. */
  churnLevels: ChurnRiskLevel[]
  /** Engagement-score levels to keep; empty = all. */
  scoreLevels: ScoreLevel[]
  /**
   * Governing license statuses to keep (lic.LicenseStatuses codes, 0 = no
   * license held); empty = all. "Only my trials" is this facet set to [1].
   */
  licenseStatusCodes: number[]
}

export function hasActiveFilters(filters: PortfolioFilters) {
  return (
    filters.customerIds.length > 0 ||
    filters.excludeCustomerIds.length > 0 ||
    filters.excludeUserIds.length > 0 ||
    filters.owners.length > 0 ||
    filters.churnLevels.length > 0 ||
    filters.scoreLevels.length > 0 ||
    filters.licenseStatusCodes.length > 0
  )
}

const CUSTOMERS_PARAM = "pf-customers"
const EXCLUDE_CUSTOMERS_PARAM = "pf-exclude-customers"
const EXCLUDE_USERS_PARAM = "pf-exclude-users"
const OWNERS_PARAM = "pf-owners"
const CHURN_PARAM = "pf-churn"
const SCORE_PARAM = "pf-activity"
const LICENSE_PARAM = "pf-license"

const CHURN_LEVEL_SET = new Set<string>(CHURN_FILTER_LEVELS)
const SCORE_LEVEL_SET = new Set<string>(SCORE_FILTER_LEVELS)

/**
 * URL-backed timeframe for ONE time-series section (each section has its
 * own param, so the activity chart and the geography map re-window
 * independently and stay shareable).
 */
export function usePortfolioTimeframe(paramName: string) {
  const [searchParams, setSearchParams] = useSearchParams()

  const raw = searchParams.get(paramName) as PortfolioTimeframeKey
  const key = TIMEFRAME_KEYS.has(raw) ? raw : DEFAULT_TIMEFRAME

  // Resolved once per key change — quarters carry fixed dates, and trailing
  // windows drifting within a session doesn't matter at day granularity.
  const window = useMemo(() => resolveTimeframe(key), [key])

  function setKey(next: PortfolioTimeframeKey) {
    setSearchParams({ [paramName]: next === DEFAULT_TIMEFRAME ? "" : next })
  }

  return { key, window, setKey }
}

function parseList(raw: string | null): string[] {
  if (!raw) return []
  return raw.split(",").filter((value) => value !== "")
}

function serializeList(values: string[]): string {
  // Empty string drops the param entirely (see utils.createSearchParams).
  return values.join(",")
}

/**
 * URL-backed global filter state, so a filtered Portfolio view is
 * shareable/bookmarkable like every other backoffice deep link.
 */
export function usePortfolioFilters() {
  const [searchParams, setSearchParams] = useSearchParams()

  const rawCustomers = searchParams.get(CUSTOMERS_PARAM)
  const rawExcludeCustomers = searchParams.get(EXCLUDE_CUSTOMERS_PARAM)
  const rawExcludeUsers = searchParams.get(EXCLUDE_USERS_PARAM)
  const rawOwners = searchParams.get(OWNERS_PARAM)
  const rawChurn = searchParams.get(CHURN_PARAM)
  const rawScore = searchParams.get(SCORE_PARAM)
  const rawLicense = searchParams.get(LICENSE_PARAM)

  // Stable identity per param values: the filters object feeds the heavy
  // derivation memos in PortfolioTab, which must not recompute on unrelated
  // re-renders.
  const filters: PortfolioFilters = useMemo(
    () => ({
      customerIds: parseList(rawCustomers),
      excludeCustomerIds: parseList(rawExcludeCustomers),
      excludeUserIds: parseList(rawExcludeUsers)
        .map(Number)
        .filter((id) => Number.isFinite(id)),
      owners: parseList(rawOwners),
      churnLevels: parseList(rawChurn).filter((level) =>
        CHURN_LEVEL_SET.has(level),
      ) as ChurnRiskLevel[],
      scoreLevels: parseList(rawScore).filter((level) =>
        SCORE_LEVEL_SET.has(level),
      ) as ScoreLevel[],
      licenseStatusCodes: parseList(rawLicense)
        .map(Number)
        .filter((code) => Number.isFinite(code)),
    }),
    [
      rawCustomers,
      rawExcludeCustomers,
      rawExcludeUsers,
      rawOwners,
      rawChurn,
      rawScore,
      rawLicense,
    ],
  )

  function setCustomerIds(ids: string[]) {
    setSearchParams({ [CUSTOMERS_PARAM]: serializeList(ids) })
  }

  function setExcludeCustomerIds(ids: string[]) {
    setSearchParams({ [EXCLUDE_CUSTOMERS_PARAM]: serializeList(ids) })
  }

  function setExcludeUserIds(ids: number[]) {
    setSearchParams({
      [EXCLUDE_USERS_PARAM]: serializeList(ids.map(String)),
    })
  }

  function setOwners(owners: string[]) {
    setSearchParams({ [OWNERS_PARAM]: serializeList(owners) })
  }

  function setChurnLevels(levels: ChurnRiskLevel[]) {
    setSearchParams({ [CHURN_PARAM]: serializeList(levels) })
  }

  function setScoreLevels(levels: ScoreLevel[]) {
    setSearchParams({ [SCORE_PARAM]: serializeList(levels) })
  }

  function setLicenseStatusCodes(codes: number[]) {
    setSearchParams({ [LICENSE_PARAM]: serializeList(codes.map(String)) })
  }

  function clearFilters() {
    setSearchParams({
      [CUSTOMERS_PARAM]: "",
      [EXCLUDE_CUSTOMERS_PARAM]: "",
      [EXCLUDE_USERS_PARAM]: "",
      [OWNERS_PARAM]: "",
      [CHURN_PARAM]: "",
      [SCORE_PARAM]: "",
      [LICENSE_PARAM]: "",
    })
  }

  return {
    filters,
    setCustomerIds,
    setExcludeCustomerIds,
    setExcludeUserIds,
    setOwners,
    setChurnLevels,
    setScoreLevels,
    setLicenseStatusCodes,
    clearFilters,
  }
}
